/**
 * DashboardGreeting — eyebrow date + H1 + subtitle shared by all three
 * dashboard states. Copy varies by state per spec §5.1.
 *
 * Time-of-day picker (Good morning / afternoon / evening) is a pure helper —
 * exported for unit testing.
 */

import type { DashboardState } from '../useDashboardState';
import { t } from '@/lib/i18n';
import { formatDate } from '@/lib/format';
import styles from './DashboardGreeting.module.css';

export interface DashboardGreetingProps {
  state: DashboardState;
  firstName: string;
  /** Required when state === 'populated' — drives the subtitle wording. */
  runningJobs?: number;
  /** Required when state === 'populated' — drives the subtitle wording. */
  needsInputCount?: number;
}

export function pickTimeOfDayGreeting(hour: number): string {
  if (hour < 12) return t('Good morning');
  if (hour < 18) return t('Good afternoon');
  return t('Good evening');
}

function pickPopulatedSubtitle(runningJobs: number, needsInputCount: number): string {
  if (needsInputCount > 0) {
    return t('{n} thing needs you. The rest of the team is moving.', {
      n: String(needsInputCount),
    });
  }
  if (runningJobs > 0) {
    return t("Everything's quiet — your team is working.");
  }
  return t('Your team is idle.');
}

export function DashboardGreeting({
  state,
  firstName,
  runningJobs = 0,
  needsInputCount = 0,
}: DashboardGreetingProps) {
  const now = new Date();
  const eyebrow = formatDate(now, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  }).toUpperCase();

  let title: string;
  let subtitle: string;

  switch (state) {
    case 'empty':
      title = t('Welcome to Linqage, {name}.', { name: firstName });
      subtitle = t(
        "Your workspace is empty. Start by hiring your first Member, or just describe the work you need done and we'll help you figure out who should do it.",
      );
      break;
    case 'onboarding':
      title = t('Welcome to Linqage, {name}.', { name: firstName });
      subtitle = t(
        "Your team is forming. Describe the work you need done and we'll get them started.",
      );
      break;
    case 'populated':
      title = t('{greeting}, {name}.', {
        greeting: pickTimeOfDayGreeting(now.getHours()),
        name: firstName,
      });
      subtitle = pickPopulatedSubtitle(runningJobs, needsInputCount);
      break;
  }

  return (
    <header className={styles.root}>
      <p className={styles.eyebrow} data-testid="dashboard-greeting-eyebrow">
        {eyebrow}
      </p>
      <h1 className={styles.title}>{title}</h1>
      <p className={styles.subtitle} data-testid="dashboard-greeting-subtitle">
        {subtitle}
      </p>
    </header>
  );
}
